import React, { useState, forwardRef, useImperativeHandle } from "react";
import cx from "classnames";
import PropTypes from "prop-types";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import { ArrowBack, ArrowForward } from "@material-ui/icons";

import CircularProgress from "@material-ui/core/CircularProgress";

import styles from "assets/jss/material-dashboard-pro-react/components/tableStyle";

const useStyles = makeStyles((theme) => ({
  ...styles,
  paginatorContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 2),
    width: "50%",
    marginLeft: "auto",
  },
  paginatorButton: {
    margin: theme.spacing(0, 1),
    "&:disabled": {
      opacity: 0.5,
    },
  },
  paginatorInfo: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
}));

const CustomTable = forwardRef((props, ref) => {
  const classes = useStyles();
  const {
    tableHead,
    tableData,
    tableHeaderColor,
    hover,
    colorsColls,
    coloredColls,
    customCellClasses,
    customClassesForCells,
    striped,
    tableShopping,
    customHeadCellClasses,
    customHeadClassesForCells,
    rowsPerPage,
    totalRows,
    totalFilters,
    changePage,
    loading,
  } = props;

  // Estado para la paginación
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = Math.ceil(
    (totalRows > 0 ? totalRows : tableData.length) / rowsPerPage
  );

  // Obtener los datos de la página actual
  const paginatedData = tableData.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );

  // Manejo del cambio de página
  const handlePageChange = (direction) => {
    const newCurrentPage =
      direction === "next"
        ? Math.min(currentPage + 1, totalPages - 1)
        : Math.max(currentPage - 1, 0);
    setCurrentPage(newCurrentPage);
    changePage(newCurrentPage);
  };

  const handleResetPage = (newPage) => {
    setCurrentPage(newPage);
    changePage(newPage);
  };

  useImperativeHandle(ref, () => ({
    resetPage: handleResetPage,
    nextPage: () => handlePageChange("next"),
    prevPage: () => handlePageChange("prev"),
    getCurrentPage: () => currentPage,
  }));

  return (
    <div className={classes.tableResponsive}>
      <Table className={classes.table}>
        {tableHead !== undefined ? (
          <TableHead className={classes[tableHeaderColor]}>
            <TableRow className={classes.tableRow + " " + classes.tableRowHead}>
              {tableHead.map((prop, key) => {
                let tableCellClasses =
                  classes.tableHeadCell +
                  " " +
                  classes.tableCell +
                  " " +
                  cx({
                    [customHeadCellClasses[
                      customHeadClassesForCells.indexOf(key)
                    ]]: customHeadClassesForCells.indexOf(key) !== -1,
                    [classes.tableShoppingHead]: tableShopping,
                    [classes.tableHeadFontSize]: !tableShopping,
                  });
                tableCellClasses +=
                  typeof prop === "object" ? prop.className : "";
                tableCellClasses = tableCellClasses.replace(/undefined/g, "");
                return (
                  <TableCell className={tableCellClasses} key={key}>
                    {typeof prop === "object" ? prop.name : prop}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
        ) : null}
        <TableBody>
          {paginatedData.map((row, key) => (
            
            <TableRow
              key={key}
              hover={hover}
              className={cx(classes.tableRowBody, {
                [classes.tableRowHover]: hover,
                [classes.tableStripedRow]: striped && key % 2 === 0,
              })}
            >
              {row.map((cell, index) => {
                let tableCellClasses =
                  classes.tableCell +
                  " " +
                  cx({
                    [classes[colorsColls[coloredColls.indexOf(index)]]]:
                      coloredColls.indexOf(index) !== -1,
                    [customCellClasses[customClassesForCells.indexOf(index)]]:
                      customClassesForCells.indexOf(index) !== -1,
                  });
                tableCellClasses +=
                  typeof cell?.className === "string" ? cell.className : "";
                tableCellClasses = tableCellClasses.replace(/undefined/g, "");
                return (
                  <TableCell className={tableCellClasses} key={index}>
                    {cell?.type === "url" ? (
                      <a
                        href={cell?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {cell?.label || cell?.name}
                      </a>
                    ) : (
                      <>{typeof cell?.name === "string" ? cell.name : cell}</>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          {loading && (
            <TableRow>
              <TableCell
                colSpan={tableHead.length}
                style={{ textAlign: "center" }}
              >
                <CircularProgress size="30px" style={{ color: "black" }} />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Paginador estilizado */}
      <div className={classes.paginatorContainer}>
        <IconButton
          className={classes.paginatorButton}
          disabled={currentPage === 0}
          onClick={() => handlePageChange("prev")}
        >
          <ArrowBack />
        </IconButton>
        <Typography className={classes.paginatorInfo}>
          Page {currentPage + 1} of {totalPages}
        </Typography>
        <IconButton
          className={classes.paginatorButton}
          disabled={currentPage === totalPages - 1}
          onClick={() => handlePageChange("next")}
        >
          <ArrowForward />
        </IconButton>
      </div>
    </div>
  );
});

CustomTable.defaultProps = {
  tableHeaderColor: "gray",
  hover: false,
  colorsColls: [],
  coloredColls: [],
  striped: false,
  customCellClasses: [],
  customClassesForCells: [],
  customHeadCellClasses: [],
  customHeadClassesForCells: [],
  rowsPerPage: 2, // Default to 5 rows per page
};

CustomTable.propTypes = {
  tableHeaderColor: PropTypes.oneOf([
    "warning",
    "primary",
    "danger",
    "success",
    "info",
    "rose",
    "gray",
  ]),
  tableHead: PropTypes.arrayOf(PropTypes.string),
  tableData: PropTypes.array.isRequired,
  hover: PropTypes.bool,
  coloredColls: PropTypes.arrayOf(PropTypes.number),
  colorsColls: PropTypes.array,
  customCellClasses: PropTypes.arrayOf(PropTypes.string),
  customClassesForCells: PropTypes.arrayOf(PropTypes.number),
  customHeadCellClasses: PropTypes.arrayOf(PropTypes.string),
  customHeadClassesForCells: PropTypes.arrayOf(PropTypes.number),
  striped: PropTypes.bool,
  tableShopping: PropTypes.bool,
  rowsPerPage: PropTypes.number, // Número de filas por página
};

export default CustomTable;
